import { Box, Typography, Link, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { useLocation, Link as RouterLink } from 'react-router-dom';

// import { generateWebPageSchema, renderMultipleStructuredData } from 'utils/structuredData';
import { GEO_PAGE_DESCRIPTIONS } from 'static/geoPageInfo';
import useClientMediaQuery from 'utils/useClientMediaQuery';

const notFoundImage = `${process.env.REACT_APP_BASE_URL}/img/static/page-not-found/404-logo.png`;

const helpfulLinks = [
    { to: '/media-service', label: 'Media Service' },
    { to: '/data-service', label: 'Data Service' },
    { to: '/#contact-us', label: 'Contact Us' }
];

const PageNotFound = () => {
    const location = useLocation();
    const theme = useTheme();
    const matchDownSm = useClientMediaQuery(theme.breakpoints.down('md'), { defaultMatches: false });

    const contentTitleStyle = {
        background: 'linear-gradient(90deg, #E15C12 0%, #FCB336 44.5%);',
        fontSize: matchDownSm ? '24px' : '36px',
        '-webkit-background-clip': 'text',
        '-webkit-text-fill-color': 'transparent',
        fontWeight: 600,
        lineHeight: '100%',
        marginBottom: matchDownSm ? '24px' : '40px'
    };

    const baseUrl = process.env.REACT_APP_BASE_URL;
    const pageUrl = `${baseUrl}${location.pathname}`;
    const pageTitle = 'Page Not Found | Hotmob - Data-Centric Media Company';
    const pageDescription = GEO_PAGE_DESCRIPTIONS.PAGE_NOT_FOUND;
    // Schema injection disabled for 404 (noindex page)
    // const structuredData = [
    //     generateWebPageSchema({
    //         title: pageTitle,
    //         description: pageDescription,
    //         path: location.pathname
    //     })
    // ];

    return (
        <Box
            component="main"
            sx={(t) => ({
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                px: 2,
                py: 4,
                [t.breakpoints.down('md')]: {
                    minHeight: 'calc(100vh - 256px)'
                }
            })}
        >
            <Helmet>
                <html lang="en" />
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={pageUrl} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:site_name" content="Hotmob" />
                <meta property="og:locale" content="en_US" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />

                {/* Robots */}
                <meta name="robots" content="noindex, nofollow" />
                <meta name="googlebot" content="noindex, nofollow" />

                {/* Structured Data */}
                {/* {renderMultipleStructuredData(structuredData)} */}
            </Helmet>
            <Box
                component="img"
                alt="404 - Page not found"
                src={notFoundImage}
                sx={(t) => ({
                    width: '100%',
                    maxWidth: '500px',
                    height: 'auto',
                    display: 'block',
                    margin: '0 auto',
                    [t.breakpoints.down('md')]: {
                        maxWidth: '300px'
                    }
                })}
            />
            <Typography component="h1" variant="h2" sx={{ mt: 3, textAlign: 'center', ...contentTitleStyle }}>
                404 – Page Not Found
            </Typography>
            <Typography
                variant="body2"
                sx={(t) => ({
                    color: '#787878',
                    fontSize: '18px',
                    fontWeight: 400,
                    lineHeight: '30px',
                    marginBottom: '24px',
                    textAlign: 'center',
                    [t.breakpoints.down('md')]: {
                        fontSize: '16px',
                        lineHeight: '25px'
                    }
                })}
            >
                The page you&apos;re looking for doesn&apos;t exist or has been moved.
            </Typography>
            <Button
                component={RouterLink}
                to="/"
                variant="contained"
                size="large"
                sx={{
                    mb: 3,
                    borderRadius: '100px',
                    fontWeight: 600,
                    padding: '10px 14px',
                    background: 'linear-gradient(89.51deg, #E15C12 0.42%, #FCB336 101.32%)',
                    color: '#FFFFFF',
                    '&:hover': {
                        background: 'linear-gradient(89.51deg, #E15C12 0.42%, #FCB336 101.32%)',
                        color: '#FFFFFF'
                    }
                }}
            >
                Return to Homepage
            </Button>
            <Typography
                variant="body2"
                sx={(t) => ({
                    color: '#787878',
                    fontSize: '18px',
                    fontWeight: 400,
                    lineHeight: '30px',
                    marginBottom: '8px',
                    [t.breakpoints.down('md')]: {
                        fontSize: '16px',
                        lineHeight: '25px'
                    }
                })}
            >
                Or explore our services:
            </Typography>
            <Box component="nav" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {helpfulLinks.map(({ to, label }) => (
                    <Link
                        key={to}
                        component={RouterLink}
                        to={to}
                        variant="body1"
                        sx={(t) => ({
                            fontSize: '20px',
                            fontWeight: 400,
                            lineHeight: '40px',
                            color: '#787878',
                            textDecorationColor: '#787878',
                            px: 1,
                            [t.breakpoints.down('md')]: {
                                fontSize: '16px',
                                textDecorationColor: 'transparent'
                            }
                        })}
                    >
                        {label}
                    </Link>
                ))}
            </Box>
        </Box>
    );
};

export default PageNotFound;
